import { useState, useEffect } from 'react';

/**
 * useMediaQuery - Matches a CSS media query via matchMedia API.
 *
 * Returns true when the query matches. Updates on resize/orientation change.
 * This is preferred over CSS-based dual-rendering (hidden md:block / md:hidden)
 * because it avoids subtle CSS interactions (overflow-x + display toggle)
 * and renders only one layout at a time.
 *
 * @example
 * ```tsx
 * const isDesktop = useMediaQuery('(min-width: 768px)');
 * return isDesktop ? <DesktopTable /> : <MobileCards />;
 * ```
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    const mq = window.matchMedia(query);
    setMatches(mq.matches);

    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [query]);

  return matches;
}
